import { useState } from 'react'
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '../lib/auth'
import { Alert, Logo, Spinner, ThemeToggle, cx } from './ui'

const NAV = [
  { to: '/app', end: true, label: 'Огляд', icon: 'home' },
  { to: '/app/keys', label: 'API-ключі', icon: 'key' },
  { to: '/app/usage', label: 'Використання', icon: 'chart' },
  { to: '/app/history', label: 'Історія QR', icon: 'grid' },
  { to: '/app/billing', label: 'Тариф і оплата', icon: 'card' },
  { to: '/app/settings', label: 'Налаштування', icon: 'cog' },
]

const ICONS: Record<string, string> = {
  home: 'M3 10.5 12 3l9 7.5M5 9.5V21h14V9.5',
  key: 'M15 7a4 4 0 1 1-3.9 5H7v3H4v-3H2.5L11 12a4 4 0 0 1 4-5Z',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  card: 'M2 8h20M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z',
  cog: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 7 19.4a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0-1.2-2.9H1a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 2.3 7a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H7a1.7 1.7 0 0 0 1-1.5V1a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5',
}

export function AppLayout() {
  const { user, loading, logout } = useAuth()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center">
        <Spinner className="h-8 w-8 text-brand-600" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur dark:border-slate-800 dark:bg-slate-950/85">
        <div className="container-page flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Меню"
              className="grid h-9 w-9 place-items-center rounded-xl text-slate-500 lg:hidden"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
              </svg>
            </button>
            <Link to="/">
              <Logo />
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/generator" className="btn-ghost hidden text-sm sm:inline-flex">
              Генератор
            </Link>
            <ThemeToggle />
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium leading-tight">{user.fullName ?? user.email}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              className="grid h-9 w-9 place-items-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-rose-600 dark:hover:bg-slate-800"
              aria-label="Вийти"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <div className="container-page flex flex-1 gap-8 py-8">
        <aside
          className={cx(
            'w-56 shrink-0 lg:block',
            menuOpen
              ? 'fixed inset-x-0 top-16 z-30 border-b border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950'
              : 'hidden',
          )}
        >
          <nav className="sticky top-24 space-y-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  cx(
                    'flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition',
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white',
                  )
                }
              >
                {/* Tailwind's spacing scale has no 4.5 — an h-4.5 here silently
                    left the icon unsized and it rendered at full box size. */}
                <svg
                  viewBox="0 0 24 24"
                  className="h-[18px] w-[18px] shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d={ICONS[item.icon]} />
                </svg>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1">
          {!user.emailVerified && (
            <Alert tone="warning" title="Підтвердьте email">
              Доки адресу не підтверджено, API повертатиме{' '}
              <code className="code">403 email_not_verified</code>. Перевірте пошту —
              ми надіслали посилання.
            </Alert>
          )}
          <div className={cx(!user.emailVerified && 'mt-6')}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
